import React, {useEffect, useState} from "react";
import {Button, Card, Divider, NavBar} from "antd-mobile";
import {getNavBarContainerStyle} from "../../../utils/deviceUtils";
import {useNavigate} from "react-router-dom";
import networkService from "../../../services/NetworkService";
import {useNoIconToast} from "../../../hooks/useCustomToast"
import DeliveryDataService, {FailedDeliveryOrder, uploadFailedPhotos} from "../../../services/DeliveryDataService";
import {DeliveringPath} from "../../../enums/DeliveryNavigatePathEnum";
import { getDeliveringListPath } from "../../../utils/deliveryRouteUtils";
import EmptyState from "../../../components/EmptyState";
import {timestampToDate} from "../../../utils/dateTimeUtils";
import {formatLocalizedDateTime} from "../../../utils/dateFormatUtils";
import useHistoryControl from "../../../hooks/useHistoryControl";
import { useDeliveryTaskOrderOperations } from "../store/deliveryStore";
import {t} from "../../../i18n";
import { formatStopNumber } from '../../../utils/stopNumberUtils';
import {WEB_TEMPORARY_GRAY_ENABLED} from "../../../utils/grayscaleDriverUtils";

interface FailedUploadProps {
}

const FailedUploadsPage: React.FC<FailedUploadProps> = () => {
    const [failedUploads, setFailedUploads] = useState<FailedDeliveryOrder[]>([]);
    const [isOnline, setIsOnline] = useState(networkService.isNetworkAvailable());
    const {showNoIconToast} = useNoIconToast();
    const [isLoading, setIsLoading] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const {handleDelivered,handleDeliveryFailed} = useDeliveryTaskOrderOperations();
    const [notification, setNotification] = useState<boolean>(false);
    const [deliveringPath, setDeliveringPath] = useState<string>(DeliveringPath.DELIVERY_DELIVERING);

    const webTempGrayEnabled =  localStorage.getItem(WEB_TEMPORARY_GRAY_ENABLED);

    useEffect(() => {
        const resolveDeliveringPath = async () => {
            const path = await getDeliveringListPath();
            setDeliveringPath(path);
        };
        resolveDeliveringPath();
    }, []);
    const { handleBackClick } = useHistoryControl(deliveringPath);
    useEffect(() => {
        //初始化网络状态
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        networkService.addEventListener('online', handleOnline);
        networkService.addEventListener('offline', handleOffline);

        return () => {
            networkService.removeEventListener('online', handleOnline);
            networkService.removeEventListener('offline', handleOffline);
        };
    }, []);

    // 获取失败的上传数据
    useEffect(() => {
        const getFailedOrders = async () => {
            try {
                setIsLoading(true);
                // 使用await正确处理异步调用
                const orders = await DeliveryDataService.getAllDeliveryOrders();
                setFailedUploads(orders);
            } catch (error) {
                console.error('Failed to fetch orders:', error);
            } finally {
                setIsLoading(false);
            }
        };

        getFailedOrders();
    }, [notification]);

    // 处理Upload All点击
    const handleUploadAll = async () => {
        if (!isOnline) {
            showNoIconToast('The network connection is unavailable, please try again later','center');
            return;
        }

        if (failedUploads.length === 0) {
            showNoIconToast('There is no data to be uploaded','center');
            return;
        }

        if (isUploading) {
            return; // 防止重复点击
        }

        setIsUploading(true);
        for (const order of failedUploads) {
            //根据type进行不同重试逻辑
            const type = order.type;
            try {
                // 过滤有效照片
                const validPhotos = order.photos.filter(item => item?.photo?.objectKey && item?.photo?.url).map(item => item.photo);
                
                // 上传照片
                const photoKeys = await uploadFailedPhotos(validPhotos, order.trackingNumber) || [];
                
                // 准备照片信息（按来源分组）
                const prepareImagesInfo = () => {
                    const cameraPhotos: Array<{s3Key: string, latitude: number, longitude: number}> = [];
                    let firstGalleryPhotoGPS: {latitude: number, longitude: number} | null = null;
                    let hasGalleryPhotos = false;
                    
                    validPhotos.forEach((photo, index) => {
                        const s3Key = photoKeys[index];
                        if (!s3Key) return;
                        
                        if (photo.source === 'camera') {
                            cameraPhotos.push({
                                s3Key: s3Key,
                                latitude: photo.latitude || 0,
                                longitude: photo.longitude || 0
                            });
                        } else if (photo.source === 'gallery') {
                            // ✅ 记录第一张相册照片的GPS（照片自己保存的GPS）
                            if (!firstGalleryPhotoGPS) {
                                firstGalleryPhotoGPS = {
                                    latitude: photo.latitude || 0,
                                    longitude: photo.longitude || 0
                                };
                            }
                            hasGalleryPhotos = true;
                        } else {
                            // 如果没有 source，默认为拍照
                            cameraPhotos.push({
                                s3Key: s3Key,
                                latitude: photo.latitude || 0,
                                longitude: photo.longitude || 0
                            });
                        }
                    });
                    
                    return {
                        cameraPhotos,
                        uploadPhotos: (hasGalleryPhotos && firstGalleryPhotoGPS) ? firstGalleryPhotoGPS : null
                    };
                };
                
                const imagesInfo = prepareImagesInfo();
                
                // ✅ 优先使用 completionLocation（完成时的GPS），否则使用 location
                const completionLocation = order.completionLocation || order.location;
                
                if (type === 'delivered'){
                    await handleDelivered({
                        trackingNumber: order.trackingNumber,
                        location: {
                            latitude: completionLocation?.latitude || 0.0,
                            longitude: completionLocation?.longitude || 0.0
                        },
                        deliveredAt: order.deliveredAt || new Date().toISOString(),
                        podImageS3KeysJson: photoKeys, // ✅ 保留旧字段
                        cameraPhotos: imagesInfo.cameraPhotos, // ✅ 新增字段
                        uploadPhotos: imagesInfo.uploadPhotos, // ✅ 新增字段
                        id: order.id,
                        ...(order.deliveryMethod ? { deliveryMethod: order.deliveryMethod } : {})
                    },false);
                }else {
                    await handleDeliveryFailed({
                        trackingNumber: order.trackingNumber,
                        location: {
                            latitude: completionLocation?.latitude || 0.0,
                            longitude: completionLocation?.longitude || 0.0
                        },
                        exceptionReasonCode: order.exceptionReasonCode ?? '',
                        id: order.id,
                        exceptionImageS3KeysJson: photoKeys, // ✅ 保留旧字段
                        cameraPhotos: imagesInfo.cameraPhotos, // ✅ 新增字段
                        uploadPhotos: imagesInfo.uploadPhotos, // ✅ 新增字段
                        ...(order.exceptionSubReasons != null && order.exceptionSubReasons.length > 0
                            ? { exceptionSubReasons: order.exceptionSubReasons }
                            : {})
                    },false);
                }


                // 提交成功后删除本地缓存
                await DeliveryDataService.deleteDeliveryOrder(order.trackingNumber);
            } catch (error: any) {
                console.error(`提交订单 ${order.trackingNumber} 失败`, error);
                if (!error.networkError) {
                    await DeliveryDataService.deleteDeliveryOrder(order.trackingNumber);
                }
            } finally {
                setNotification(prev => !prev);
                await new Promise(resolve => setTimeout(resolve, 1000));
            }
        }
        setIsUploading(false);
    };

    return (
        <div className="flex flex-col h-screen bg-white">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                onBack={handleBackClick}
                right={
                    <Button
                        color='primary'
                        fill='none'
                        onClick={handleUploadAll}
                        disabled={!isOnline || failedUploads.length === 0 || isUploading}
                        loading={isUploading}
                    >
                        {t(`pages.delivery.failed.uploadAll`)}
                    </Button>
                }
            >
                <strong>{webTempGrayEnabled ? t('pages.delivery.temporary.uploading.title') : t(`pages.useMenu.failedUploads`)}</strong>
                </NavBar>
            </div>
            <Divider/>
            {isLoading ? (
                <div className="flex items-center justify-center h-full">
                    <p className="text-gray-500">Loading...</p>
                </div>
            ) : failedUploads.length > 0 ? (
                <div className="bg-gray-100">
                    {failedUploads.map((order, index) => (
                        <Card key={order.trackingNumber} className="my-2 rounded-lg">
                            <div className="flex gap-6">
                                <div className="flex flex-col items-center justify-center gap-0.5">
                                    {(() => {
                                        const stopNumber = formatStopNumber(parseInt(order.stopNumber || '0'), order.subStopNumber);
                                        return (
                                            <div className={`${stopNumber.length > 4 ? 'text-2xl' : 'text-3xl'} font-bold whitespace-nowrap`}>
                                                {stopNumber}
                                            </div>
                                        );
                                    })()}
                                    <div className="text-xs">{t(`pages.delivery.failed.stop`)}</div>
                                </div>

                                <div className="flex flex-col gap-1">
                                    <h3 className="text-base font-semibold">{order.trackingNumber}</h3>
                                    <p>{webTempGrayEnabled ? t('pages.delivery.temporary.uploading.hintMessage') : t(`pages.delivery.failed.failedUploads`)}</p>
                                    {order.updatedAt && (
                                        <p>{t(`pages.delivery.failed.saveAt`)} {formatLocalizedDateTime(timestampToDate(order.updatedAt))}</p>
                                    )}

                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            ) : (
                <EmptyState/>
            )}

        </div>
    );
};

export default FailedUploadsPage;